import { getUserInfo } from "../../services/localStorage";
import {
  canAccessJ5PendingCaptures,
  isAdminUser,
} from "../../services/authService";
import {
  getAdminHotelOrders,
  getOrdersByEmail,
} from "../../services/fhpService";
import { parseHotelGetOrdersEnvelope } from "../orders/agentHotelOrdersEnvelope";
import {
  collectHotelCxlDropRiskAlerts,
  summarizeHotelCxlDropRiskAlerts,
} from "../../utils/orders/resolveHotelCxlDropRisk";

const CACHE_TTL_MS = 60 * 1000;
const ADMIN_SCAN_PAGE_SIZE = 100;
const ADMIN_SCAN_MAX_PAGES = 3;

let memoryCache = { key: "", storedAt: 0, value: null };
let inFlight = null;

function parseAdminHotelOrdersEnvelope(axiosLike) {
  const rawData = axiosLike?.data;
  let body;
  if (typeof rawData === "string") {
    try {
      body = JSON.parse(rawData);
    } catch {
      body = {};
    }
  } else {
    body = rawData && typeof rawData === "object" ? rawData : {};
  }
  return {
    orders: Array.isArray(body.orders) ? body.orders : [],
    total: Number(body.totalCount ?? body.total ?? 0) || 0,
    totalPages: Number(body.totalPages ?? 1) || 1,
    hasNextPage: Boolean(body.hasNextPage),
  };
}

export function viewerSeesGlobalHotelCxlRisk() {
  return isAdminUser() || canAccessJ5PendingCaptures();
}

function cacheKey(viewerIsOps, email) {
  return `${viewerIsOps ? "ops" : "agent"}:${String(email || "").trim().toLowerCase()}`;
}

function emptyResult(viewerIsOps) {
  return {
    alerts: [],
    summary: summarizeHotelCxlDropRiskAlerts([]),
    viewerIsOps,
    error: null,
  };
}

async function fetchAgentHotelOrders(email) {
  const response = await getOrdersByEmail({ email });
  const parsed = parseHotelGetOrdersEnvelope(response);
  if (parsed.hotelsBarrier) return [];
  return parsed.orders;
}

async function fetchOpsPendingHotelOrders(email) {
  const collected = [];
  let page = 1;
  let hasMore = true;
  while (hasMore && page <= ADMIN_SCAN_MAX_PAGES) {
    const response = await getAdminHotelOrders({
      email,
      page,
      pageSize: ADMIN_SCAN_PAGE_SIZE,
      orderStatus: "pending",
      filtersUserSelected: true,
    });
    const parsed = parseAdminHotelOrdersEnvelope(response);
    collected.push(...parsed.orders);
    const loadedAll =
      parsed.total > 0
        ? collected.length >= parsed.total
        : parsed.orders.length < ADMIN_SCAN_PAGE_SIZE;
    hasMore = Boolean(parsed.hasNextPage) && !loadedAll && parsed.orders.length > 0;
    page += 1;
  }
  return collected;
}

async function loadAlertsNow() {
  const viewerIsOps = viewerSeesGlobalHotelCxlRisk();
  const email = getUserInfo()?.email || "";
  if (!email && !viewerIsOps) return emptyResult(viewerIsOps);

  let orders = [];
  try {
    if (viewerIsOps) {
      try {
        orders = await fetchOpsPendingHotelOrders(email);
      } catch {
        orders = email ? await fetchAgentHotelOrders(email) : [];
      }
    } else {
      orders = await fetchAgentHotelOrders(email);
    }
  } catch {
    return { ...emptyResult(viewerIsOps), error: "load_failed" };
  }

  const alerts = collectHotelCxlDropRiskAlerts(orders);
  return {
    alerts,
    summary: summarizeHotelCxlDropRiskAlerts(alerts),
    viewerIsOps,
    error: null,
  };
}

export async function fetchHotelCxlDropRiskAlerts({ force = false } = {}) {
  const viewerIsOps = viewerSeesGlobalHotelCxlRisk();
  const email = getUserInfo()?.email || "";
  const key = cacheKey(viewerIsOps, email);
  const now = Date.now();
  if (
    !force &&
    memoryCache.key === key &&
    now - memoryCache.storedAt < CACHE_TTL_MS &&
    memoryCache.value
  ) {
    return memoryCache.value;
  }
  if (!force && inFlight && inFlight.key === key) {
    return inFlight.promise;
  }
  const promise = loadAlertsNow()
    .then((value) => {
      memoryCache = { key, storedAt: Date.now(), value };
      return value;
    })
    .finally(() => {
      if (inFlight?.promise === promise) inFlight = null;
    });
  inFlight = { key, promise };
  return promise;
}

export function peekHotelCxlDropRiskAlertsCache() {
  if (!memoryCache.value) return null;
  if (Date.now() - memoryCache.storedAt >= CACHE_TTL_MS) return null;
  return memoryCache.value;
}
